import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { User, Shield, LogOut, Info, ChevronDown, Eye, FileText, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = "/images/logo-tuprima.png";
const WHATSAPP_SUPPORT = "573206313258";

export function Header() {
  const { user, isAdmin, logout, isAuthenticated } = useAuth();

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Hola TuPrima 👋\n\nNecesito información sobre mi solicitud de devolución.`
    );
    window.open(`https://wa.me/${WHATSAPP_SUPPORT}?text=${message}`, '_blank');
  };

  return (
    <header className="sticky top-0 z-50 glass-header">
      <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <img 
            src={LOGO_URL} 
            alt="Tu Prima" 
            className="h-10 w-auto"
          />
        </Link>

        {/* Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {/* Information Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-slate-600 hover:text-slate-900"
                data-testid="nav-info-dropdown"
              >
                <Info className="w-4 h-4 mr-1.5" />
                Información
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-56 rounded-xl">
              <DropdownMenuItem asChild>
                <Link to="/transparencia" className="cursor-pointer flex items-center" data-testid="nav-transparency">
                  <Eye className="w-4 h-4 mr-2 text-emerald-500" />
                  Transparencia
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/terms" className="cursor-pointer flex items-center" data-testid="nav-terms">
                  <FileText className="w-4 h-4 mr-2 text-slate-500" />
                  Términos y Condiciones
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* User Actions */}
        <div className="flex items-center gap-2">
          {/* WhatsApp Button */}
          <Button 
            onClick={openWhatsApp}
            size="sm" 
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="header-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Contactar</span>
          </Button>

          {/* Admin Access (if authenticated) */}
          {isAuthenticated && isAdmin && (
            <Link to="/admin">
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-full"
                data-testid="admin-link"
              >
                <Shield className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Admin</span>
              </Button>
            </Link>
          )}

          {/* User Menu (if authenticated) */}
          {isAuthenticated && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-full bg-slate-100 hover:bg-slate-200"
                  data-testid="user-menu-trigger"
                >
                  <User className="w-5 h-5 text-slate-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer" data-testid="profile-link">
                    <User className="w-4 h-4 mr-2" />
                    Mi Perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout} 
                  className="text-destructive cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
